import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Bot, Info, Loader2, Lock, X } from "lucide-react";
import { customFetch } from "@workspace/api-client-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { WhatsAppInput } from "@/components/ui/whatsapp-input";
import { ModalShell } from "@/components/ui/modal-shell";
import { useConfirm } from "@/components/ui/confirm-dialog";
import { useToast } from "@/hooks/use-toast";
import { UltraBadge } from "@/components/UltraBadge";
import { extractApiErrorMessage } from "@/lib/api-error";
import { formatBRDateTimeShort } from "@/lib/datetime";
import {
  WHATSAPP_BOT_QK,
  displayPhone,
  numeroDaLista,
  type BotConnection,
  type BotSettings,
  type WhatsappBotState,
} from "@/lib/whatsapp-bot";

/**
 * Configurações → Robô do WhatsApp (Ultra): conecta o WhatsApp da loja por QR
 * e responde sozinho — saudação, fora do horário e status do pedido. A primeira
 * conexão já liga o robô (Pedro, 15/10/2026: «Ela que desliga se precisar»);
 * reconectar mantém o que a loja deixou.
 */

const PAUSE_OPTIONS: Array<{ minutes: number; label: string }> = [
  { minutes: 15, label: "15 min" },
  { minutes: 30, label: "30 min" },
  { minutes: 60, label: "1 hora" },
  { minutes: 180, label: "3 horas" },
  { minutes: 360, label: "6 horas" },
  { minutes: 720, label: "12 horas" },
  { minutes: 1440, label: "24 horas" },
];

const TEXTAREA =
  "w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 resize-none [field-sizing:content]";

type Patch = Partial<BotSettings> & { enabled?: boolean };

const AI_INFO_MAX = 2000;
const DICA_COM_IA = "Com a Conversa com IA ligada, esta mensagem sai na primeira mensagem do cliente e a IA continua a conversa.";

export function WhatsAppBotSection({
  podeUsar,
  mostrarSelo,
  showProducts,
}: {
  podeUsar: boolean;
  mostrarSelo: boolean;
  showProducts: boolean;
}) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const confirm = useConfirm();
  const [qrOpen, setQrOpen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: WHATSAPP_BOT_QK,
    queryFn: () => customFetch<WhatsappBotState>("/api/whatsapp-bot"),
  });

  const save = useMutation({
    mutationFn: (patch: Patch) =>
      customFetch<{ enabled: boolean; settings: BotSettings }>("/api/whatsapp-bot", {
        method: "PATCH",
        body: JSON.stringify(patch),
      }),
    onMutate: (patch) => {
      qc.setQueryData<WhatsappBotState>(WHATSAPP_BOT_QK, (old) => {
        if (!old?.settings) return old;
        const { enabled, ...rest } = patch;
        return { ...old, enabled: enabled ?? old.enabled, settings: { ...old.settings, ...rest } };
      });
    },
    onSuccess: (res) =>
      qc.setQueryData<WhatsappBotState>(WHATSAPP_BOT_QK, (old) => (old ? { ...old, enabled: res.enabled, settings: res.settings } : old)),
    onError: (err) => {
      toast({ title: extractApiErrorMessage(err, "Não foi possível salvar."), variant: "destructive" });
      qc.invalidateQueries({ queryKey: WHATSAPP_BOT_QK });
    },
  });

  const disconnect = useMutation({
    mutationFn: () => customFetch<{ connection: BotConnection }>("/api/whatsapp-bot/disconnect", { method: "POST" }),
    onSuccess: (res) => {
      qc.setQueryData<WhatsappBotState>(WHATSAPP_BOT_QK, (old) => (old ? { ...old, connection: res.connection } : old));
      toast({ title: "WhatsApp desconectado." });
    },
    onError: (err) => toast({ title: extractApiErrorMessage(err, "Não foi possível desconectar."), variant: "destructive" }),
  });

  const conn = data?.connection ?? null;
  const connected = conn?.state === "connected";
  const s = data?.settings ?? null;

  const askDisconnect = async () => {
    if (
      await confirm({
        title: "Desconectar o WhatsApp?",
        description: "O robô para de responder até você conectar de novo.",
        confirmLabel: "Desconectar",
        destructive: true,
      })
    ) {
      disconnect.mutate();
    }
  };

  return (
    <Card className="p-6 space-y-4">
      <div className="flex items-center gap-2 text-lg font-semibold flex-wrap">
        <Bot className="w-5 h-5 text-primary" /> Robô do WhatsApp
        {mostrarSelo && <UltraBadge />}
      </div>
      <p className="text-xs text-muted-foreground -mt-2">
        Responde seus clientes no WhatsApp da loja enquanto você atende.
      </p>

      {isLoading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="w-4 h-4 animate-spin" /> Carregando…
        </div>
      ) : (
        <>
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border px-3 py-2.5">
            <div className="min-w-0">
              <div className="text-sm font-medium">{connected ? "WhatsApp conectado" : "WhatsApp não conectado"}</div>
              {connected && (
                <div className="text-xs text-muted-foreground truncate">
                  {displayPhone(conn?.phone)}
                  {conn?.profileName ? ` · ${conn.profileName}` : ""}
                </div>
              )}
            </div>
            {connected ? (
              <Button type="button" size="sm" variant="outline" onClick={askDisconnect} disabled={disconnect.isPending}>
                {disconnect.isPending && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
                Desconectar
              </Button>
            ) : (
              <Button type="button" size="sm" onClick={() => setQrOpen(true)} disabled={!podeUsar} data-testid="bot-connect">
                Conectar
              </Button>
            )}
          </div>

          {data?.enabled && <SaudacaoDoWhatsappBusiness em={data.settings?.whatsappBusinessAutomaticaEm} />}

          {s && data && (
            <BotSettingsForm
              data={data}
              settings={s}
              connected={connected}
              podeUsar={podeUsar}
              showProducts={showProducts}
              onSave={(patch) => save.mutate(patch)}
            />
          )}
        </>
      )}

      {qrOpen && (
        <QrModal
          onClose={() => setQrOpen(false)}
          onConnected={async () => {
            setQrOpen(false);
            // O aviso segue o que o servidor devolve depois da conexão: ligado na primeira, como a loja deixou na reconexão.
            await qc.invalidateQueries({ queryKey: WHATSAPP_BOT_QK });
            const ligado = qc.getQueryData<WhatsappBotState>(WHATSAPP_BOT_QK)?.enabled === true;
            toast({
              title: "WhatsApp conectado.",
              description: ligado
                ? "O robô já começa ligado. Para parar, desligue aqui ou tire a etiqueta Robô atendendo da conversa."
                : "Ligue o robô quando quiser começar.",
            });
          }}
        />
      )}
    </Card>
  );
}

const SETE_DIAS_MS = 7 * 24 * 60 * 60 * 1000;

// Saudação automática do WhatsApp Business (a do app, não a nossa): com ela ligada o cliente recebe duas saudações, e
// só a lojista desliga, no app (a UAZAPI não alcança). Com o robô ligado: a orientação; se o servidor viu a do app nos
// últimos 7 dias, o aviso em âmbar no lugar dela (decisão do Pedro em 14/10/2026).
function SaudacaoDoWhatsappBusiness({ em }: { em: string | null | undefined }) {
  const quando = em ? new Date(em) : null;
  const recente = !!quando && !Number.isNaN(quando.getTime()) && Date.now() - quando.getTime() <= SETE_DIAS_MS;
  if (recente) {
    return (
      <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800" data-testid="bot-wa-business-aviso">
        <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
        <p className="text-xs text-amber-800 dark:text-amber-300">
          Seu WhatsApp Business ainda mandou a saudação automática em {formatBRDateTimeShort(quando)}. Desligue em{" "}
          <strong>Ferramentas comerciais</strong> → <strong>Mensagem de saudação</strong> (e <strong>Mensagem de ausência</strong>) para
          ficar só a do robô.
        </p>
      </div>
    );
  }
  return (
    <div className="flex items-start gap-2 p-3 rounded-lg bg-muted/50 border" data-testid="bot-wa-business-orientacao">
      <Info className="w-4 h-4 text-muted-foreground shrink-0 mt-0.5" />
      <p className="text-xs text-muted-foreground">
        Desligue a saudação do WhatsApp Business: no app, <strong>Ferramentas comerciais</strong> → <strong>Mensagem de saudação</strong> →
        desligar. Faça o mesmo na <strong>Mensagem de ausência</strong>. Assim, quem cumprimenta é só o robô.
      </p>
    </div>
  );
}

function SwitchRow({
  label,
  hint,
  checked,
  disabled,
  onChange,
  testId,
}: {
  label: string;
  hint?: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (v: boolean) => void;
  testId?: string;
}) {
  return (
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <Label className="text-sm font-medium">{label}</Label>
        {hint && <p className="text-xs text-muted-foreground mt-1">{hint}</p>}
      </div>
      <Switch className="mt-0.5 shrink-0" checked={checked} disabled={disabled} onCheckedChange={onChange} data-testid={testId} />
    </div>
  );
}

// Texto salvo ao sair do campo. Vazio volta ao padrão (null no servidor).
function BotText({
  value,
  fallback,
  onSave,
  testId,
}: {
  value: string | null;
  fallback: string;
  onSave: (v: string | null) => void;
  testId: string;
}) {
  const [draft, setDraft] = useState(value ?? fallback);
  useEffect(() => setDraft(value ?? fallback), [value, fallback]);
  const isCustom = value !== null && value !== fallback;
  return (
    <div className="space-y-1">
      <textarea
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => {
          const v = draft.trim();
          const next = !v || v === fallback ? null : v;
          if (next !== value) onSave(next);
        }}
        rows={3}
        className={TEXTAREA}
        data-testid={testId}
      />
      {isCustom && (
        <button
          type="button"
          onClick={() => onSave(null)}
          className="text-xs text-muted-foreground hover:text-destructive"
        >
          Restaurar padrão
        </button>
      )}
    </div>
  );
}

function BotSettingsForm({
  data,
  settings: s,
  connected,
  podeUsar,
  showProducts,
  onSave,
}: {
  data: WhatsappBotState;
  settings: BotSettings;
  connected: boolean;
  podeUsar: boolean;
  showProducts: boolean;
  onSave: (patch: Patch) => void;
}) {
  return (
    <div className="space-y-4 border-t pt-4">
      <SwitchRow
        label="Robô ligado"
        checked={data.enabled}
        // Ligar exige o plano e o WhatsApp conectado; desligar, nunca.
        disabled={!data.enabled && (!podeUsar || !connected)}
        onChange={(v) => onSave({ enabled: v })}
        testId="bot-enabled"
      />

      <div className="space-y-2 border-t pt-4">
        <SwitchRow
          label="Saudação"
          hint={showProducts ? "Primeira mensagem do dia de cada cliente, com o link do cardápio." : "Primeira mensagem do dia de cada cliente, com o link de agendamento."}
          checked={s.greetingEnabled}
          onChange={(v) => onSave({ greetingEnabled: v })}
          testId="bot-greeting-enabled"
        />
        {s.aiEnabled && <p className="text-xs text-muted-foreground" data-testid="bot-greeting-ai-hint">{DICA_COM_IA}</p>}
        {s.greetingEnabled && (
          <BotText value={s.greetingText} fallback={data.defaults.greeting} onSave={(v) => onSave({ greetingText: v })} testId="bot-greeting-text" />
        )}
      </div>

      <div className="space-y-2 border-t pt-4">
        <SwitchRow
          label="Fora do horário"
          hint="Avisa que a loja está fechada e manda o link."
          checked={s.awayEnabled}
          onChange={(v) => onSave({ awayEnabled: v })}
          testId="bot-away-enabled"
        />
        {s.aiEnabled && <p className="text-xs text-muted-foreground" data-testid="bot-away-ai-hint">{DICA_COM_IA}</p>}
        {s.awayEnabled && (
          <BotText value={s.awayText} fallback={data.defaults.away} onSave={(v) => onSave({ awayText: v })} testId="bot-away-text" />
        )}
      </div>

      {(s.greetingEnabled || s.awayEnabled) && (
        <p className="text-xs text-muted-foreground -mt-2">
          Use <code className="bg-muted px-1 rounded">{"{link}"}</code> para o link e{" "}
          <code className="bg-muted px-1 rounded">{"{loja_nome}"}</code> para o nome da loja.
        </p>
      )}

      {showProducts && (
        <div className="border-t pt-4">
          <SwitchRow
            label="Status do pedido"
            hint="Manda as Mensagens de Status sozinho, sem abrir o WhatsApp."
            checked={s.statusEnabled}
            onChange={(v) => onSave({ statusEnabled: v })}
            testId="bot-status-enabled"
          />
        </div>
      )}

      <div className="flex items-start justify-between gap-3 border-t pt-4">
        <div className="min-w-0">
          <Label className="text-sm font-medium">Quando você responde pelo celular</Label>
          {/* Um texto só: o robô é um, com tudo (a IA vem ligada; decisão do Pedro em 14/10/2026). */}
          <p className="text-xs text-muted-foreground mt-1" data-testid="bot-pause-hint">
            O robô pausa nessa conversa pelo tempo escolhido ou até ela voltar para <strong>Robô atendendo</strong>.
          </p>
        </div>
        <select
          className="border rounded-md p-2 text-sm bg-background h-10 shrink-0"
          value={String(s.pauseMinutes)}
          onChange={(e) => onSave({ pauseMinutes: Number(e.target.value) })}
          data-testid="bot-pause"
        >
          {PAUSE_OPTIONS.some((o) => o.minutes === s.pauseMinutes) ? null : (
            <option value={String(s.pauseMinutes)}>{s.pauseMinutes} min</option>
          )}
          {PAUSE_OPTIONS.map((o) => (
            <option key={o.minutes} value={String(o.minutes)}>
              {o.label}
            </option>
          ))}
        </select>
      </div>

      <ListaDeNumeros
        titulo="Números de teste"
        dica={
          s.testNumbers.length > 0
            ? "O robô só responde estes números. Tire todos para valer para os clientes."
            : "Para testar antes, adicione um número: o robô só responderá ele."
        }
        numeros={s.testNumbers}
        onChange={(testNumbers) => onSave({ testNumbers })}
        inputId="bot-test-number"
        testId="bot-test-numbers"
      />

      <ListaDeNumeros
        titulo="Números que o robô não responde"
        dica="Família, fornecedores e quem mais escreve no WhatsApp da loja sem ser cliente. O robô não responde as mensagens desses números."
        numeros={s.ignoredNumbers ?? []}
        fixos={s.numerosFixosIgnorados ?? []}
        onChange={(ignoredNumbers) => onSave({ ignoredNumbers })}
        inputId="bot-ignored-number"
        testId="bot-ignored-numbers"
      />

      <ConversaComIa settings={s} connected={connected} podeUsar={podeUsar} showProducts={showProducts} onSave={onSave} />
    </div>
  );
}

// Lista de números editável (adicionar e remover): os de teste e os que o robô não responde. Os `fixos` (o suporte do
// Venda no Zap, que o robô nunca responde) aparecem sem o X e ficam fora do que a tela salva.
function ListaDeNumeros({
  titulo,
  dica,
  numeros,
  fixos = [],
  onChange,
  inputId,
  testId,
}: {
  titulo: string;
  dica: string;
  numeros: string[];
  fixos?: Array<{ numero: string; motivo: string }>;
  onChange: (numeros: string[]) => void;
  inputId: string;
  testId: string;
}) {
  const [number, setNumber] = useState("");
  const [ddi, setDdi] = useState("55");
  const add = () => {
    const digits = number.replace(/\D/g, "");
    if (digits.length < 8) return;
    onChange([...numeros, `${ddi}${digits}`]);
    setNumber("");
  };
  return (
    <div className="space-y-2 border-t pt-4" data-testid={testId}>
      <Label htmlFor={inputId} className="text-sm font-medium">{titulo}</Label>
      <p className="text-xs text-muted-foreground -mt-1">{dica}</p>
      {(fixos.length > 0 || numeros.length > 0) && (
        <div className="flex flex-wrap gap-1.5">
          {fixos.map((f) => (
            // No celular o rótulo desce inteiro para a linha de baixo (o número não quebra no meio).
            <span key={f.numero} className="inline-flex flex-wrap items-center gap-x-1.5 gap-y-0.5 rounded-2xl bg-muted px-2.5 py-1 text-xs" data-testid="numero-fixo">
              <span className="inline-flex items-center gap-1 whitespace-nowrap">
                <Lock className="w-3 h-3 text-muted-foreground" aria-hidden="true" />
                {numeroDaLista(f.numero)}
              </span>
              <span className="whitespace-nowrap text-muted-foreground">{f.motivo} · sempre</span>
            </span>
          ))}
          {numeros.map((n) => (
            <span key={n} className="inline-flex items-center gap-1 rounded-full bg-muted px-2.5 py-1 text-xs">
              {numeroDaLista(n)}
              <button
                type="button"
                aria-label="Remover número"
                className="text-muted-foreground hover:text-destructive"
                onClick={() => onChange(numeros.filter((x) => x !== n))}
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}
      <div className="flex gap-2">
        <div className="flex-1 min-w-0">
          <WhatsAppInput
            id={inputId}
            value={number}
            onChange={setNumber}
            ddi={ddi}
            onDdiChange={setDdi}
            placeholder="(00) 00000-0000"
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                add();
              }
            }}
          />
        </div>
        <Button type="button" variant="outline" onClick={add} disabled={number.replace(/\D/g, "").length < 8}>
          Adicionar
        </Button>
      </div>
    </div>
  );
}

// Conversa com IA (Ultra, decisão do Pedro em 14/10/2026): o robô tira dúvidas com os dados da loja e chama a loja
// quando não sabe. O texto é salvo ao sair do campo, como os outros da seção.
function ConversaComIa({
  settings: s,
  connected,
  podeUsar,
  showProducts,
  onSave,
}: {
  settings: BotSettings;
  connected: boolean;
  podeUsar: boolean;
  showProducts: boolean;
  onSave: (patch: Patch) => void;
}) {
  const ligada = !!s.aiEnabled;
  const [info, setInfo] = useState(s.aiInfo ?? "");
  useEffect(() => setInfo(s.aiInfo ?? ""), [s.aiInfo]);
  return (
    <div className="space-y-3 rounded-md border p-4" data-testid="bot-ai-card">
      <SwitchRow
        label="Conversa com IA"
        hint={`Responde as dúvidas dos clientes com os dados da loja e manda o link para ${showProducts ? "pedir" : "agendar"}.`}
        checked={ligada}
        // Como o «Robô ligado»: ligar exige o plano e o WhatsApp conectado; desligar, nunca.
        disabled={!ligada && (!podeUsar || !connected)}
        onChange={(v) => onSave({ aiEnabled: v })}
        testId="bot-ai-enabled"
      />
      <div className="space-y-1.5 border-t pt-3">
        <Label htmlFor="bot-ai-info" className="text-sm font-medium">Informações para o robô</Label>
        <textarea
          id="bot-ai-info"
          value={info}
          maxLength={AI_INFO_MAX}
          onChange={(e) => setInfo(e.target.value)}
          onBlur={() => {
            const next = info.trim() || null;
            if (next !== (s.aiInfo ?? null)) onSave({ aiInfo: next });
          }}
          rows={4}
          placeholder="Ex.: Entregamos em 40 a 50 minutos. Não aceitamos vale-refeição. Bolo de aniversário só por encomenda, com 2 dias de antecedência."
          className={TEXTAREA}
          data-testid="bot-ai-info"
        />
        <div className="flex items-start justify-between gap-3 text-xs text-muted-foreground">
          <p>Só o que não está no cardápio. Preços, taxas e horários ele já lê do sistema.</p>
          <span className="shrink-0 tabular-nums" data-testid="bot-ai-info-count">
            {info.length.toLocaleString("pt-BR")}/{AI_INFO_MAX.toLocaleString("pt-BR")}
          </span>
        </div>
      </div>
      {/* Linhas só de leitura: no celular o texto vai embaixo do rótulo; a partir do sm, ao lado. */}
      <div className="flex flex-col gap-1 border-t pt-3 sm:flex-row sm:items-start sm:justify-between sm:gap-3">
        <Label className="text-sm font-medium sm:shrink-0">Quando não souber</Label>
        <p className="text-xs text-muted-foreground sm:text-right" data-testid="bot-ai-quando-nao-souber">
          põe a conversa na etiqueta <strong>Chamar equipe</strong>, marca como não lida e segue atendendo. No WhatsApp comum, a
          etiqueta aparece em <strong>Listas</strong>.
        </p>
      </div>
      <div className="flex flex-col gap-1 border-t pt-3 sm:flex-row sm:items-start sm:justify-between sm:gap-3">
        <Label className="text-sm font-medium sm:shrink-0">Parar o robô numa conversa</Label>
        <p className="text-xs text-muted-foreground sm:text-right" data-testid="bot-ai-parar-conversa">
          tire a conversa da etiqueta <strong>Robô atendendo</strong> (no WhatsApp comum, em <strong>Listas</strong>). Para ele voltar,
          ponha de novo.
        </p>
      </div>
      {/* Loja só de serviços não tem pedido pelo link. */}
      {showProducts && (
        <div className="flex flex-col gap-1 border-t pt-3 sm:flex-row sm:items-start sm:justify-between sm:gap-3">
          <Label className="text-sm font-medium sm:shrink-0">Pedido pelo link</Label>
          <p className="text-xs text-muted-foreground sm:text-right" data-testid="bot-ai-pedido-link">
            o robô confirma que recebeu, deixa a conversa como não lida e põe a etiqueta <strong>Novo pedido</strong>. No WhatsApp
            comum, a etiqueta aparece em <strong>Listas</strong>.
          </p>
        </div>
      )}
      <div className="flex flex-col gap-1 border-t pt-3 sm:flex-row sm:items-start sm:justify-between sm:gap-3">
        <Label className="text-sm font-medium sm:shrink-0">Testar antes</Label>
        <p className="text-xs text-muted-foreground sm:text-right">Com números de teste na lista, só eles recebem as respostas do robô.</p>
      </div>
    </div>
  );
}

function QrModal({ onClose, onConnected }: { onClose: () => void; onConnected: () => void }) {
  const [conn, setConn] = useState<BotConnection | null>(null);
  const [qr, setQr] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const done = useRef(false);

  const finish = () => {
    if (done.current) return;
    done.current = true;
    onConnected();
  };

  const apply = (c: BotConnection) => {
    setConn(c);
    if (c.qrcode) setQr(c.qrcode);
    if (c.state === "connected") finish();
  };

  const start = useMutation({
    mutationFn: () => customFetch<{ connection: BotConnection }>("/api/whatsapp-bot/connect", { method: "POST" }),
    onMutate: () => {
      setError(null);
      setQr(null);
    },
    onSuccess: (res) => apply(res.connection),
    onError: (err) => setError(extractApiErrorMessage(err, "Não foi possível conectar.")),
  });

  useEffect(() => {
    start.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // O QR troca sozinho a cada ~20 s no WhatsApp: a consulta traz o atual.
  const waiting = conn?.state === "connecting";
  useEffect(() => {
    if (!waiting) return;
    const t = setInterval(() => {
      customFetch<{ connection: BotConnection }>("/api/whatsapp-bot/status")
        .then((res) => apply(res.connection))
        .catch(() => undefined);
    }, 3000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [waiting]);

  const expired = !!conn && !start.isPending && conn.state !== "connecting" && conn.state !== "connected";

  return (
    <ModalShell
      open
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
      title="Conectar o WhatsApp da loja"
      size="sm"
      footer={
        <div className="flex justify-end">
          <Button type="button" variant="outline" onClick={onClose}>
            Fechar
          </Button>
        </div>
      }
    >
      <div className="space-y-4 text-center">
        <p className="text-sm text-muted-foreground">
          No celular da loja, abra o WhatsApp → Aparelhos conectados → Conectar um aparelho.
        </p>
        <div className="mx-auto flex h-64 w-64 items-center justify-center rounded-lg border bg-white">
          {qr && waiting ? (
            <img src={qr} alt="QR code para conectar o WhatsApp" className="h-60 w-60" data-testid="bot-qr" />
          ) : error || expired ? null : (
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          )}
          {(error || expired) && (
            <div className="space-y-3 px-4">
              <p className="text-sm">{error || "O QR code expirou."}</p>
              <Button type="button" size="sm" onClick={() => start.mutate()} disabled={start.isPending}>
                Gerar outro
              </Button>
            </div>
          )}
        </div>
      </div>
    </ModalShell>
  );
}
